import styled, { css, keyframes } from 'styled-components';

import { fontVariantMap, getVariant } from '@/themes/typographyUtilis';

const cardContainerBorderAnimation = (borderColor: string, defaultBorderColor: string) => keyframes`
  0% {
    border-color: ${borderColor};
  }
  50% {
    border-color: ${defaultBorderColor};
  }
  100% {
    border-color: ${borderColor};
  }
`;

export const CardContainer = styled.div<{
  borderColor?: string;
  isNew?: boolean;
}>`
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid ${({ borderColor, theme }) => borderColor || theme.colors.border};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.background.light};
  color: ${({ theme }) => theme.colors.primaryText};
  cursor: pointer;

  ${({ isNew, borderColor, theme }) =>
    isNew &&
    borderColor &&
    theme.pulseNewReportsInSidebar &&
    css`
      animation: ${cardContainerBorderAnimation(borderColor, theme.colors.border)} 1.5s infinite;
    `}
`;

export const CardHeader = styled.div`
  position: relative;
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 7px 16px;
  background-color: ${({ theme }) => theme.colors.background.extraLight};
  color: ${({ theme }) => theme.colors.secondaryText};
`;

export const CardHeaderRightContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 12px;
  font-size: 11px;
`;

export const CardHeaderDates = styled.p`
  color: ${({ theme }) => theme.colors.secondaryText};
  ${getVariant({ variant: 'body3-normal' })}
  font-size: calc(${fontVariantMap['body3-normal'].fontSize} * ${({ theme }) =>
    theme.reportListItemFontSizeFactor});
  line-height: calc(
    ${fontVariantMap['body3-normal'].lineHeight} *
      ${({ theme }) => theme.reportListItemFontSizeFactor}
  );
`;

export const CardHeaderIconsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CardUnpinButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  display: none;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 100%;
  margin: 0;
  border: none;
  padding: 0;
  background-color: ${({ theme }) => theme.colors.background.extraLight};
  cursor: pointer;

  ${CardContainer}:hover & {
    display: flex;
  }
`;

export const CardContent = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding: 12px 16px;
  font-size: 14px;

  &:empty {
    display: none;
  }
`;

export const CardTitle = styled.div`
  color: ${({ theme }) => theme.colors.primaryText};
  ${getVariant({ variant: 'body1-normal' })}
  font-size: calc(${fontVariantMap['body1-normal'].fontSize} * ${({ theme }) =>
    theme.reportListItemFontSizeFactor});
  line-height: calc(
    ${fontVariantMap['body1-normal'].lineHeight} *
      ${({ theme }) => theme.reportListItemFontSizeFactor}
  );
`;

export const Country = styled.span`
  color: ${({ theme }) => theme.colors.active};
`;

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 0 0;
`;

export const ReportCategory = styled.span`
  display: flex;
  align-items: center;
  text-transform: uppercase;
`;

export const CategoryBadge = styled.div<{ backgroundColor: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 19px;
  min-width: 19px;
  height: 19px;
  margin-right: 3px;
  border-radius: 50%;
  background-color: ${({ backgroundColor }) => backgroundColor};
`;

export const CardCategoryBadgeText = styled.p`
  color: ${({ theme }) => theme.colors.primaryText};
  ${getVariant({ variant: 'caption-bold' })}
  font-size: calc(${fontVariantMap['caption-bold'].fontSize} * ${({ theme }) =>
    theme.reportListItemFontSizeFactor});
  line-height: calc(
    ${fontVariantMap['caption-bold'].lineHeight} *
      ${({ theme }) => theme.reportListItemFontSizeFactor}
  );
`;

export const CategoryMore = styled.span`
  margin-left: 6px;
  color: ${({ theme }) => theme.colors.primaryText};
  ${getVariant({ variant: 'body2-normal' })}
  font-size: calc(${fontVariantMap['body2-normal'].fontSize} * ${({ theme }) =>
    theme.reportListItemFontSizeFactor});
  line-height: calc(
    ${fontVariantMap['body2-normal'].lineHeight} *
      ${({ theme }) => theme.reportListItemFontSizeFactor}
  );
`;

export const MarkAsButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  border: none;
  padding: 0;
  background: none;
  cursor: pointer;
`;
